import { QueryErrorResetBoundary } from '@tanstack/react-query';
import {
  createRootRoute,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { lazy, useEffect } from 'react';
import { showDevtools } from '@/lib/utils/env';

// Zero bytes in production — static import would bundle it regardless of the
// DEV guard because tree-shaking can't eliminate side-effectful imports.
const TanStackRouterDevtools = showDevtools
  ? lazy(() =>
      import('@tanstack/router-devtools').then(m => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

import { createPortal } from 'react-dom';
import { Toaster } from 'sonner';
import { AddressMissingBanner } from '@/components/shared/AddressMissingBanner';
import { ClientHeader } from '@/components/shared/ClientHeader';
import DocsHeader from '@/components/shared/DocsHeader';
import { EnvironmentBanner } from '@/components/shared/EnvironmentBanner';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { IntercomConsentBanner } from '@/components/shared/IntercomConsentBanner';
import { IntercomLauncherDismiss } from '@/components/shared/IntercomLauncherDismiss';
import { ManagementHeader } from '@/components/shared/ManagementHeader';
import NavigationProgress from '@/components/shared/NavigationProgress';
import { OldDashboardBanner } from '@/components/shared/OldDashboardBanner';
import { OnboardingTour } from '@/components/shared/OnboardingTour';
import Sidebar from '@/components/shared/Sidebar';
import { EXPANDED_SIDEBAR_WIDTH } from '@/components/shared/Sidebar/styles';
import { TermsOfUseGate } from '@/components/shared/TermsOfUseGate';
import { UploadGuard } from '@/components/shared/UploadGuard';
import { useBreadcrumbTrail } from '@/hooks/useBreadcrumbTrail';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsWideScreen } from '@/hooks/useIsWideScreen';
import { useSidebarNav } from '@/hooks/useSidebarNav';
import { useTheme } from '@/hooks/useTheme';
import { checkAuthGuard } from '@/lib/utils/auth';
import { setDatadogUser } from '@/lib/utils/datadog';
import { identifyIntercomUser, pageChangeIntercom } from '@/lib/utils/intercom';
import { MANAGEMENT_ROOT } from '@/lib/utils/managementAccess';
import {
  bootstrapSession,
  COMPANY_SEARCH_ROUTE,
  hasCompany,
  isCompanylessAllowedPath,
  isSsoOnboardingPath,
  LOGIN_ROUTE,
  ONBOARDING_ROOT,
  SSO_CALLBACK_ROUTE,
  UNSUBSCRIBE_ROUTE,
} from '@/lib/utils/session';
import { isPathActive } from '@/lib/utils/sidebarNavHelpers';
import { useSessionStore } from '@/store/sessionStore';
import { useSidebarStore } from '@/store/sidebarStore';

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // Docs are public — accessible without authentication.
    if (location.pathname.startsWith('/docs')) return;

    // `/` is the public login screen — the one route an unauthenticated
    // visitor is *supposed* to reach. Its own `beforeLoad` forwards a
    // signed-in user to the dashboard, so no session work is needed here.
    if (location.pathname === LOGIN_ROUTE) return;

    // The SSO callback lands here mid-login, before any session exists — it
    // is the page that creates one. Guarding it like every other route would
    // bounce it straight back to LOGIN_ROUTE before it could exchange its code.
    if (location.pathname === SSO_CALLBACK_ROUTE) return;

    // The unsubscribe link carries its own credential (the email hash) and is
    // followed by someone who is not signed in. See `UNSUBSCRIBE_ROUTE`.
    if (location.pathname === UNSUBSCRIBE_ROUTE) return;

    // The SSO onboarding portal is opened by a customer's IT admin who has no
    // account; the link's token authorises it. See `SSO_ONBOARDING_ROOT`.
    if (isSsoOnboardingPath(location.pathname)) return;

    const { allowed } = await checkAuthGuard();
    if (!allowed) throw redirect({ to: LOGIN_ROUTE });
    const me = await bootstrapSession();
    const onOnboarding = location.pathname.startsWith(ONBOARDING_ROOT);
    // `me === null` means the /api/users/me fetch itself failed (network
    // error, transient 5xx) — fall through to today's behaviour rather than
    // treating a fetch failure as "companyless" and bouncing every user.
    if (me && !hasCompany(me) && !isCompanylessAllowedPath(location.pathname)) {
      throw redirect({ to: COMPANY_SEARCH_ROUTE });
    }
    if (me && hasCompany(me) && onOnboarding) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: RootLayout,
});

function RootLayout() {
  const panelOpen = useSidebarStore(s => s.panelOpen);
  const breadcrumbTrail = useBreadcrumbTrail();
  const sidebarNav = useSidebarNav();
  const isMobile = useIsMobile();
  const isWideScreen = useIsWideScreen();
  const user = useSessionStore(s => s.user);
  const pathname = useRouterState({ select: s => s.location.pathname });
  const router = useRouter();
  const isDocsRoute = pathname.startsWith('/docs');
  // Exact match: every path starts with '/'.
  const isLoginRoute = pathname === LOGIN_ROUTE;
  // Renders chromeless like the login screen and, like it, has no session to
  // re-hydrate yet — the page itself is what creates one.
  const isSsoCallbackRoute = pathname === SSO_CALLBACK_ROUTE;
  const isPreAuthRoute =
    isLoginRoute || isSsoCallbackRoute || isSsoOnboardingPath(pathname);
  const isManagementRoute = isPathActive(pathname, MANAGEMENT_ROOT);

  // Re-hydrate session when in-memory store is empty (e.g. after Vite HMR
  // replaces the module before any navigation re-runs `beforeLoad`).
  // Skip on public /docs — no session there, and a 401 would bounce to login.
  useEffect(() => {
    if (user || isDocsRoute || isPreAuthRoute) return;
    let cancelled = false;
    void (async () => {
      const me = await bootstrapSession();
      if (cancelled || !me) return;
      if (!hasCompany(me) && !isCompanylessAllowedPath(pathname)) {
        void router.navigate({ to: COMPANY_SEARCH_ROUTE });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isDocsRoute, isPreAuthRoute, pathname, router]);

  // Attach the authenticated user to the RUM session for error/session
  // correlation. No-op in local dev where RUM isn't initialised.
  useEffect(() => {
    if (user) setDatadogUser(user);
  }, [user]);

  // Identify the authenticated user to Intercom (no-op until cookie consent
  // is granted — see `identifyIntercomUser`).
  useEffect(() => {
    identifyIntercomUser(user);
  }, [user]);
  // Ping Intercom on every navigation (refreshes "last seen", surfaces
  // URL-targeted messages) — parity with the old dashboard. No-op until consent.
  useEffect(() => {
    pageChangeIntercom();
  }, [pathname]);
  useTheme();

  // Login (and the SSO callback that lands mid-login) renders chromeless: no
  // sidebar, no header, no opt-out banner, and no fixed-inset shell — the page
  // owns the full viewport and scrolls itself on a short screen. Returning
  // before the shell is built also keeps the sidebar and breadcrumb machinery
  // from mounting for a visitor with no session.
  if (isPreAuthRoute) {
    return (
      <>
        {/* Before the shell exists, because which environment you are handing
            your credentials to matters most at the moment you sign in. */}
        <EnvironmentBanner />
        {/* No NavigationProgress here: it is a route-transition indicator, and
            login has no navigation to report — it only rendered as a stuck
            2px brand-coloured stripe across the top of the screen. */}
        {/* Boundary, same as the shell below has. Without one, anything that
            throws while rendering login leaves a blank white page with nothing
            on screen and nothing in the console to work from. */}
        <ErrorBoundary resetKey={pathname} onReset={() => router.invalidate()}>
          <Outlet />
        </ErrorBoundary>
        {createPortal(
          <Toaster expand richColors position='bottom-right' />,
          document.body,
        )}
      </>
    );
  }

  const isDocs = isDocsRoute;
  // Collapsed, the sidebar is the icon-only rail: 56px below 1600px, 64px above
  // (matches its `w-14` / `min-[1600px]:w-16` classes — no labels to fit).
  // Expanded, it is the single column carrying both levels, which needs label
  // width (matches `w-62`). The hover flyout is an overlay and takes no column.
  const railWidth = isWideScreen ? 64 : 56;
  const sidebarWidth =
    isDocs || isMobile ? 0 : panelOpen ? EXPANDED_SIDEBAR_WIDTH : railWidth;

  return (
    <div className='fixed inset-0 flex flex-col overflow-hidden'>
      {/* Above everything, docs included: which environment you are on is not
          a property of whichever page you happen to be looking at. */}
      <EnvironmentBanner />
      {/* Opt-out row above the shell, so it pushes the app down rather than
          overlaying it — the docs pages have their own chrome and skip it. */}
      {!isDocs && <OldDashboardBanner />}
      <div
        className='relative min-h-0 flex-1 overflow-hidden grid transition-[grid-template-columns] duration-200'
        style={{
          gridTemplateColumns: `${sidebarWidth}px 1fr`,
          gridTemplateRows: '52px 1fr',
        }}
      >
        <NavigationProgress />
        {/* One header per area. Each composes the controls it offers, so none of
            them needs to know the others exist. */}
        {isDocs ? (
          <DocsHeader />
        ) : isManagementRoute ? (
          <ManagementHeader breadcrumbTrail={breadcrumbTrail} />
        ) : (
          <ClientHeader breadcrumbTrail={breadcrumbTrail} />
        )}
        {!isDocs && <Sidebar nav={sidebarNav} />}
        <main className='overflow-hidden row-start-2 col-start-2 flex flex-col min-h-0 h-full bg-background @container'>
          {/* Inside the client page area, so it pushes the page down rather
              than overlaying it. Not on docs (their own chrome) nor on
              management (an operator is not the company being asked). */}
          {!isDocs && !isManagementRoute && <AddressMissingBanner />}
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                resetKey={pathname}
                onReset={() => {
                  reset();
                  void router.invalidate();
                }}
              >
                <Outlet />
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </main>
        <UploadGuard />
        {/* Internal-only area: the tours explain the customer app, and an
            operator administering the platform is not their audience. */}
        {!isManagementRoute && <OnboardingTour />}
        <IntercomLauncherDismiss />
        {/* App-level cookie-consent prompt, decoupled from the tour and the
            "?" button: asks every signed-in user who hasn't decided yet, so
            tour-skippers and existing users are reached too. Self-gates on the
            authenticated + undecided condition. Excluded from management routes
            for the same reason as the tour: Intercom is customer chat support,
            and a Retraced operator (e.g. the impersonation picker) is not its
            audience. */}
        {/* Blocking, so it must render before the (non-blocking) Intercom
            banner below — both are self-gating app-level overlays excluded
            from `/management` the same way, but this one must never be
            dodged: there is no Escape/backdrop dismissal, unlike the banner. */}
        {!isManagementRoute && <TermsOfUseGate />}
        {!isManagementRoute && <IntercomConsentBanner />}
        {/* Bottom-left, away from the right edge, which is crowded: the
            Intercom bubble and its dismiss ✕ sit there, the React Query dev
            button is pinned to that corner and takes no offset prop, and a
            right-side sheet covers all of it. Still nudged up by the same
            4.25rem, because the sidebar's expand/collapse chevron owns the
            bottom-left corner itself. */}
        {showDevtools && (
          <TanStackRouterDevtools
            position='bottom-left'
            toggleButtonProps={{
              style: {
                bottom: '4.25rem',
                // Round logo button, to match the React Query one below it.
                // This version of the core renders a labelled pill; there is no
                // prop for a compact toggle, and its markup is mounted
                // imperatively, so the label is collapsed with `fontSize: 0`
                // rather than by reaching into DOM we do not own. The logo is
                // an SVG and keeps its own size.
                width: '2.25rem',
                height: '2.25rem',
                padding: 0,
                borderRadius: '9999px',
                overflow: 'hidden',
                fontSize: 0,
              },
            }}
          />
        )}
      </div>
      {createPortal(
        <Toaster expand richColors position='bottom-right' />,
        document.body,
      )}
    </div>
  );
}
